import { Transaction } from '@mysten/sui/transactions';
import { getCpcBalance } from './balance.js';
import { CPC_FRAMEWORK, DGC_TYPE } from './constants.js';
/** All owned `Coin<T>` with balance > 0 (paginated). */
export async function fetchLooseCoinsOfType(client, owner, coinType) {
    const coins = [];
    let cursor = undefined;
    for (;;) {
        const resp = await client.getCoins({ owner, coinType, cursor });
        for (const c of resp.data) {
            if (BigInt(c.balance) > 0n) {
                coins.push({
                    coinObjectId: c.coinObjectId,
                    version: c.version,
                    digest: c.digest,
                });
            }
        }
        if (!resp.hasNextPage || !resp.nextCursor)
            break;
        cursor = resp.nextCursor;
    }
    return coins;
}
/** All owned `Coin<DGC>` with balance > 0 (paginated). */
export async function fetchLooseCpcCoins(client, owner) {
    return fetchLooseCoinsOfType(client, owner, DGC_TYPE);
}
export async function fetchLooseCpcCoinIds(client, owner) {
    return (await fetchLooseCpcCoins(client, owner)).map((c) => c.coinObjectId);
}
/** Merge all loose `Coin<T>` into one, then `coin::send_funds` → owner address balance. */
export function appendConsolidateLooseCoinsOfType(tx, owner, coinType, coinObjectIds) {
    if (coinObjectIds.length === 0)
        return;
    const [primary, ...rest] = coinObjectIds;
    let target = tx.object(primary);
    for (const id of rest) {
        tx.moveCall({
            target: `${CPC_FRAMEWORK}::coin::join`,
            typeArguments: [coinType],
            arguments: [target, tx.object(id)],
        });
    }
    tx.moveCall({
        target: `${CPC_FRAMEWORK}::coin::send_funds`,
        typeArguments: [coinType],
        arguments: [target, tx.pure.address(owner)],
    });
}
/** Merge all loose `Coin<DGC>` into one, then `coin::send_funds` → owner address balance. */
export function appendConsolidateLooseCpcCoins(tx, owner, coinObjectIds) {
    appendConsolidateLooseCoinsOfType(tx, owner, DGC_TYPE, coinObjectIds);
}
/**
 * PTB: all owned `Coin<T>` → address balance.
 * Gas: DGC address balance when available (`setGasPayment([])`); else for DGC only,
 * pay gas from the first loose coin (`tx.gas`).
 * Returns `null` when there are no loose coins.
 */
export async function buildConsolidateCoinsToAddressBalance(client, owner, coinType) {
    const coins = await fetchLooseCoinsOfType(client, owner, coinType);
    if (coins.length === 0)
        return null;
    const dgcBal = await getCpcBalance(client, owner);
    const tx = new Transaction();
    const payGasFromAddressBalance = dgcBal.addressBalanceMist > 0n;
    if (payGasFromAddressBalance) {
        appendConsolidateLooseCoinsOfType(tx, owner, coinType, coins.map((c) => c.coinObjectId));
        tx.setGasPayment([]);
        return tx;
    }
    if (coinType !== DGC_TYPE) {
        throw new Error(`Cannot consolidate ${coinType}: need DGC in address balance for gas (AB=0 and no AB-gas path)`);
    }
    const [gas, ...rest] = coins;
    tx.setGasPayment([
        { objectId: gas.coinObjectId, version: gas.version, digest: gas.digest },
    ]);
    for (const c of rest) {
        tx.moveCall({
            target: `${CPC_FRAMEWORK}::coin::join`,
            typeArguments: [DGC_TYPE],
            arguments: [tx.gas, tx.object(c.coinObjectId)],
        });
    }
    tx.moveCall({
        target: `${CPC_FRAMEWORK}::coin::send_funds`,
        typeArguments: [DGC_TYPE],
        arguments: [tx.gas, tx.pure.address(owner)],
    });
    return tx;
}
/**
 * PTB: all owned `Coin<DGC>` → address balance (P-UX; no leftover fragments).
 * When address balance is empty, pay gas from a loose coin (same PTB as join/send_funds).
 * Returns `null` when there are no loose coins.
 *
 * Important: the gas-payment coin must be referenced as `tx.gas`, never also as
 * `tx.object(sameId)` — otherwise: "Mutable object ... cannot appear more than once".
 */
export async function buildConsolidateCpcCoinsToAddressBalance(client, owner) {
    return buildConsolidateCoinsToAddressBalance(client, owner, DGC_TYPE);
}
